import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import api from '../services/api'
import SortableTh, { sortRows } from '../components/SortableTh'
import Paginacao, { usePaginacao } from '../components/Paginacao'
import { useConfig } from '../contexts/ConfigContext'
import { corClara } from '../utils/cor'
import { escapeHtml } from '../utils/html'
import { useNavegacaoLista } from '../hooks/useNavegacaoLista'
import IconAcoes from '../components/icons/IconAcoes'
import IconVisualizar from '../components/icons/IconVisualizar'
import IconSinistro from '../components/icons/IconSinistro'
import IconAssistencia from '../components/icons/IconAssistencia'
import { avisar } from '../components/Avisos'
import SelecaoMultipla from '../components/SelecaoMultipla'

const seguradorasPadrao = ['Akad','Aliro','Allianz','Azul','Bradesco','Essor','HDI','Itaú','Mitsui','Porto','Suhai','Tokio Marine','Yelum']
const ramos = ['Acidentes Pessoais','Automóvel','Bike','Carta Verde','Condomínio','Empresarial','Equipamentos','Residencial','Responsabilidade Civil','Vida em Grupo','Vida Individual','Viagem']

// DATE do Postgres vira JSON como "AAAA-MM-DDT00:00:00.000Z" (meia-noite UTC) — reconstruir com o
// "Z" faz o toLocaleDateString num fuso atras de UTC (Brasil) voltar 1 dia. Por isso sempre
// descarta a hora/fuso vindos do backend e ancora de novo em meia-noite LOCAL.
const fmtData = (d) => d ? new Date(d.split('T')[0] + 'T00:00:00').toLocaleDateString('pt-BR') : '—'
const fmtValor = (v) => `R$ ${parseFloat(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`
const diasParaVencer = (d) => Math.ceil((new Date(d) - new Date()) / (1000 * 60 * 60 * 24))

const badgeDias = (dias) => {
  if (dias < 0) return { label: `Vencida há ${Math.abs(dias)}`, cls: 'badge-red' }
  if (dias <= 15) return { label: `${dias}`, cls: 'badge-red' }
  if (dias <= 30) return { label: `${dias}`, cls: 'badge-amber' }
  return { label: `${dias}`, cls: 'badge-green' }
}

const CHAVE_FILTROS_RENOVACOES = 'renovacoes.filtros'
const FILTROS_RENOVACOES_VAZIOS = { seguradoras: [], ramos: [], status: [], vigDe: '', vigAte: '' }
const lerFiltrosRenovacoes = () => {
  try { return JSON.parse(localStorage.getItem(CHAVE_FILTROS_RENOVACOES)) || {} } catch { return {} }
}
const temCriterioRenovacoes = (f) => !!(f.seguradoras.length || f.ramos.length || f.status.length || f.vigDe || f.vigAte)

// Etapas do CRM (mesmos ids/rotulos/cores do COLUNAS de CRM.jsx): com card de renovacao gerado,
// o Status da linha passa a ser a coluna em que o card esta.
const ETAPAS_CRM = {
  prospeccao:       { label: 'Novo Card',       cor: '#6B7280' },
  primeiro_contato: { label: 'Calculando',      cor: '#FFA500' },
  cotacao:          { label: 'Cotação Enviada', cor: '#FF4500' },
  proposta_enviada: { label: 'Em Negociação',   cor: '#0000CD' },
  negociacao:       { label: 'Fechado',         cor: '#008000' },
  fechamento:       { label: 'Perdido',         cor: '#B22222' },
}

// Ordem do fluxo (pedido do usuario, 27/10): Renovar -> coluna do card no CRM -> Proposta ->
// Renovado. Não Renovado = venceu sem card nem proposta de renovacao.
const STATUS_RENOVACAO = {
  a_renovar: { label: 'Renovar', cls: 'badge-blue' },
  ...Object.fromEntries(Object.entries(ETAPAS_CRM).map(([etapa, e]) => [`crm_${etapa}`, { label: e.label, cor: e.cor }])),
  proposta:     { label: 'Proposta',     cls: 'badge-amber' },
  renovado:     { label: 'Renovado',     cls: 'badge-green' },
  nao_renovado: { label: 'Não Renovado', cls: 'badge-red' },
}
const BadgeStatus = ({ st }) => st.cor
  ? <span className="badge" style={{ background: st.cor + '1F', color: st.cor }}>{st.label}</span>
  : <span className={`badge ${st.cls}`}>{st.label}</span>

export default function Renovacoes() {
  const { config } = useConfig()
  const navigate = useNavigate()
  const [apolices, setApolices] = useState([])
  const [sinistros, setSinistros] = useState([])
  const [assistencias, setAssistencias] = useState([])
  const [cardsCrm, setCardsCrm] = useState([])
  const [seguradoras, setSeguradoras] = useState(seguradorasPadrao)
  const [carregando, setCarregando] = useState(true)

  const [busca, setBusca] = useState('')
  const [mostrarSugestoesBusca, setMostrarSugestoesBusca] = useState(false)
  // Lista abre vazia e so mostra o que foi buscado/filtrado; filtro igual ao da Consulta de
  // Apolices, salvo neste navegador ate o "Limpar filtros" (pedido do usuario, 26/10).
  const [usarFiltro, setUsarFiltro] = useState(() => !!lerFiltrosRenovacoes().usarFiltro)
  const [filtros, setFiltros] = useState(() => {
    const salvos = lerFiltrosRenovacoes().filtros || {}
    const f = Object.fromEntries(Object.entries(FILTROS_RENOVACOES_VAZIOS).map(([k, v]) => [k, salvos[k] ?? v]))
    return { ...f, status: (f.status || []).filter(st => STATUS_RENOVACAO[st]) }
  })
  const setFiltro = (campo, valor) => setFiltros(f => ({ ...f, [campo]: valor }))
  useEffect(() => {
    try { localStorage.setItem(CHAVE_FILTROS_RENOVACOES, JSON.stringify({ usarFiltro, filtros })) } catch { /* sem localStorage: so nao persiste */ }
  }, [usarFiltro, filtros])

  const [sortCol, setSortCol] = useState('dias')
  const [sortDir, setSortDir] = useState('asc')
  const toggleSort = (key) => {
    if (sortCol === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc')
    else { setSortCol(key); setSortDir('asc') }
  }

  const [acoesAbertoId, setAcoesAbertoId] = useState(null)
  // Posicao do menu de Acoes em coordenadas de viewport (position:fixed) — nao pode ser
  // position:absolute relativo ao table-wrap porque esse (overflow-x:auto, que forca
  // overflow-y:auto tambem) corta o menu sempre que a tabela tem poucas linhas (ex.: busca
  // filtrando pra 1 resultado so): nem abrindo pra baixo nem pra cima sobra espaco dentro do
  // wrap pro menu inteiro. Fixed escapa desse clipping e usa a janela toda como referencia.
  const [acoesMenuPos, setAcoesMenuPos] = useState(null)
  const acoesMenuRef = useRef(null)
  const tableWrapRef = useRef(null)

  // Menu do botao Ver PDF (so quando a apolice tem endosso): { id da linha, posicao fixed }
  const [pdfMenu, setPdfMenu] = useState(null)
  const pdfMenuRef = useRef(null)
  useEffect(() => {
    if (!pdfMenu) return
    const handler = (e) => { if (pdfMenuRef.current && !pdfMenuRef.current.contains(e.target)) setPdfMenu(null) }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [pdfMenu])

  useEffect(() => {
    if (acoesAbertoId == null) return
    const handler = (e) => { if (acoesMenuRef.current && !acoesMenuRef.current.contains(e.target)) setAcoesAbertoId(null) }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [acoesAbertoId])

  // Volta de um deep-link pra Sinistros/Assistencias/Apolice: restaura a busca que estava
  // digitada (os filtros ja ficam salvos no navegador).
  useEffect(() => {
    const salvos = sessionStorage.getItem('renovacoesFiltros')
    if (!salvos) return
    sessionStorage.removeItem('renovacoesFiltros')
    try { setBusca(JSON.parse(salvos).busca || '') } catch { /* ignora json invalido */ }
  }, [])

  const carregar = () => {
    setCarregando(true)
    return Promise.all([
      api.get('/apolices'),
      api.get('/cadastro/seguradoras'),
      api.get('/sinistros').then(r => Array.isArray(r.data) ? r.data : []).catch(() => []),
      api.get('/assistencias').then(r => Array.isArray(r.data) ? r.data : []).catch(() => []),
      // Usuario sem acesso ao CRM: so nao mostra a etiqueta de card gerado
      api.get('/funil').then(r => Array.isArray(r.data) ? r.data : []).catch(() => []),
    ]).then(([apolicesRes, seguradorasRes, sinistrosRes, assistenciasRes, cardsRes]) => {
      setCardsCrm(cardsRes)
      setApolices(Array.isArray(apolicesRes.data) ? apolicesRes.data : [])
      const lista = (seguradorasRes.data || []).map(item => item.nome)
      if (lista.length) setSeguradoras(lista)
      setSinistros(sinistrosRes)
      setAssistencias(assistenciasRes)
    }).finally(() => setCarregando(false))
  }

  useEffect(() => { carregar() }, [])

  // "Gerar Card Renov." no menu Ações: cria uma Nova Negociação no CRM ja preenchida (segurado,
  // ramo, tipo Renovação Interna, e o vinculo com a apolice de origem — pra mostrar Nº da apólice/
  // Seguradora/Prêmio Líquido/% Comissão/Prêmio Total na aba Dados), pro usuario so conferir e
  // clicar em "Criar negociação" pra confirmar. Reaproveita o mesmo mecanismo de "modal pendente"
  // que o proprio CRM.jsx ja usa internamente (ex.: voltar da tela de Cliente com o formulario
  // ainda aberto).
  const gerarCardRenovacao = (a) => {
    sessionStorage.setItem('crmFormPendente', JSON.stringify({
      cliente_id: a.cliente_id,
      etapa: 'prospeccao',
      ramo: a.ramo || 'Automóvel',
      tipo: 'Renovação Interna',
      observacoes: '',
      apolice_origem_id: a.id
    }))
    navigate('/crm')
  }

  // Card de renovacao ja gerado pra apolice (vinculo apolice_origem_id). /funil vem do mais novo
  // pro mais antigo, entao fica o mais recente quando houver mais de um.
  const cardCrmPorApoliceId = new Map()
  cardsCrm.forEach(c => {
    if (c.apolice_origem_id && !cardCrmPorApoliceId.has(String(c.apolice_origem_id))) cardCrmPorApoliceId.set(String(c.apolice_origem_id), c)
  })
  const abrirCardCrm = (card) => {
    sessionStorage.setItem('crmAbrirCardId', String(card.id))
    navigate('/crm')
  }

  // PDF de um documento da cadeia: na apolice, o anexo mais recente do tipo "Apólice"; no endosso,
  // o do tipo/nome "Endosso" (senao o PDF mais recente dele). A aba e aberta antes do await pro
  // navegador nao bloquear como pop-up.
  const verPdfDocumento = async (doc) => {
    const aba = window.open('', '_blank')
    const rotulo = doc.eh_endosso ? 'endosso' : 'apólice'
    try {
      const { data: anexos } = await api.get(`/apolices/${doc.id}/anexos`)
      const pdfs = [...(Array.isArray(anexos) ? anexos : [])].filter(x => /\.pdf$/i.test(x.nome || '')).reverse()
      const re = doc.eh_endosso ? /endosso/i : /ap[oó]lice/i
      const anexo = pdfs.find(x => re.test(x.tipo || '')) || pdfs.find(x => re.test(x.nome || '')) || (doc.eh_endosso ? pdfs[0] : null)
      if (!anexo) { aba?.close(); avisar(`Nenhum PDF de ${rotulo} anexado a este documento.`); return }
      const { data } = await api.get(`/apolices/anexos/${anexo.id}/dados`)
      const [cab, base64] = data.dados.split(',')
      const bytes = Uint8Array.from(atob(base64), c => c.charCodeAt(0))
      const url = URL.createObjectURL(new Blob([bytes], { type: cab.split(':')[1].split(';')[0] }))
      if (aba) aba.location.href = url
      else window.open(url, '_blank')
    } catch (err) {
      aba?.close()
      avisar(`Erro ao abrir o PDF do ${rotulo === 'endosso' ? 'endosso' : 'apólice'}: ` + (err.response?.data?.erro || err.message))
    }
  }

  // Endosso (mesma vigencia, so um aditivo): o filho representa o estado atual da MESMA apolice,
  // entao a origem some da lista (evita duas linhas pra uma unica vigencia). Renovacao (motivo
  // Renovacao Interna/Externa, eh_endosso=false): e uma vigencia NOVA e independente — a origem
  // continua aparecendo aqui, so que com status Proposta/Renovado (ver statusRenovacao).
  const idsComFilhoEndosso = new Set(
    apolices.filter(a => a.eh_endosso && a.documento_origem_id).map(a => String(a.documento_origem_id))
  )

  // Status de renovacao e 100% derivado do status da propria proposta/apolice de renovacao (sem
  // flag manual): em_analise/pendente = processo em andamento (Proposta); emitido/vencida =
  // renovacao de fato concluida (Renovado); recusada/cancelada nao conta (como se nao existisse).
  // Se houver mais de uma tentativa de renovacao pra mesma origem, fica com a mais avancada.
  // So 'Renovação Interna' conta: 'Renovação Externa' e' seguro novo pra corretora (veio de outra
  // corretora com bonus), nunca renovacao de uma apolice da casa (regra do usuario, 07/10).
  const renovacaoPorOrigemId = new Map()
  apolices
    .filter(a => !a.eh_endosso && a.documento_origem_id && a.motivo === 'Renovação Interna')
    .forEach(a => {
      if (['recusada', 'cancelada'].includes(a.status)) return
      const chave = String(a.documento_origem_id)
      const atual = renovacaoPorOrigemId.get(chave)
      const concluida = ['emitido', 'vencida'].includes(a.status)
      if (!atual || (!['emitido', 'vencida'].includes(atual.status) && concluida)) renovacaoPorOrigemId.set(chave, a)
    })

  const statusRenovacao = (a) => {
    const renovacao = renovacaoPorOrigemId.get(String(a.id))
    if (renovacao) {
      if (['emitido', 'vencida'].includes(renovacao.status)) return { key: 'renovado', ...STATUS_RENOVACAO.renovado }
      return { key: 'proposta', ...STATUS_RENOVACAO.proposta }
    }
    const card = cardCrmPorApoliceId.get(String(a.id))
    if (card && STATUS_RENOVACAO[`crm_${card.etapa}`]) return { key: `crm_${card.etapa}`, ...STATUS_RENOVACAO[`crm_${card.etapa}`] }
    if (diasParaVencer(a.data_vencimento) < 0) return { key: 'nao_renovado', ...STATUS_RENOVACAO.nao_renovado }
    return { key: 'a_renovar', ...STATUS_RENOVACAO.a_renovar }
  }

  // Linha que e' endosso (ponta da cadeia): numero e premio exibidos sao os da APOLICE (premio do
  // endosso sozinho e' so a diferenca, as vezes R$ 0,00), com o numero do endosso embaixo — mesmo
  // criterio do relatorio de renovacoes do Corp (pedido do usuario, 26/10).
  const apolicePorId = new Map(apolices.map(a => [String(a.id), a]))
  const apoliceDaLinha = (a) => {
    let x = a
    const vistos = new Set()
    while (x.eh_endosso && x.documento_origem_id && apolicePorId.has(String(x.documento_origem_id)) && !vistos.has(x.id)) {
      vistos.add(x.id)
      x = apolicePorId.get(String(x.documento_origem_id))
    }
    return x
  }
  const numeroApolice = (a) => { const ap = apoliceDaLinha(a); return (ap.numero_apolice || ap.emissao_numero_apolice || a.numero_apolice) || '' }
  const numeroEndosso = (a) => (a.eh_endosso && a.emissao_numero_endosso) || ''
  // Cadeia da linha, da apolice ate a ponta: [apolice, endosso 1, endosso 2, ...]
  const cadeiaDaLinha = (a) => {
    const cadeia = [a]
    let x = a
    const vistos = new Set([a.id])
    while (x.eh_endosso && x.documento_origem_id && apolicePorId.has(String(x.documento_origem_id))) {
      x = apolicePorId.get(String(x.documento_origem_id))
      if (vistos.has(x.id)) break
      vistos.add(x.id)
      cadeia.unshift(x)
    }
    return cadeia
  }
  // Com endosso, o premio exibido e a soma do premio total da apolice + o de cada endosso
  // (restituicao vem negativa e desconta). Endosso recusado/cancelado nao entra.
  const premioApolice = (a) => cadeiaDaLinha(a)
    .filter(d => !d.eh_endosso || !['recusada', 'cancelada'].includes(d.status))
    .reduce((soma, d) => soma + (parseFloat(d.premio_total || d.valor_premio) || 0), 0)

  const colunasRenovacoes = [
    { key: 'vigencia',   label: 'Final da\nVigência',  get: a => a.data_vencimento || '', date: true },
    { key: 'dias',       label: 'Dias para\nVencer',   get: a => diasParaVencer(a.data_vencimento), numeric: true },
    { key: 'segurado',   label: 'Segurado',            get: a => a.cliente_nome || '' },
    { key: 'cpf',        label: 'CPF/CNPJ',            get: a => a.cliente_cpf_cnpj || '' },
    { key: 'ramo',       label: 'Ramo',                get: a => a.ramo || '' },
    { key: 'seguradora', label: 'Seguradora',          get: a => a.seguradora || '' },
    { key: 'numero',     label: 'Apólice/\nEndosso',   get: a => numeroApolice(a) },
    { key: 'premio',     label: 'Prêmio\nTotal',       get: a => parseFloat(premioApolice(a) || 0), numeric: true },
    { key: 'status',     label: 'Status',              get: a => statusRenovacao(a).label },
  ]

  // Sinistro/assistência ficam ligados a um id especifico da cadeia (a apolice-mae, um endosso...),
  // nao necessariamente a ponta que aparece aqui — por isso o vinculo e feito pelo documento_raiz_id
  // (a "apolice" como um todo), nao pelo id exato da linha exibida.
  const raizPorId = new Map(apolices.map(a => [String(a.id), String(a.documento_raiz_id || a.id)]))
  const raizesComSinistro = new Set(sinistros.map(s => raizPorId.get(String(s.apolice_id))).filter(Boolean))
  const raizesComAssistencia = new Set(assistencias.map(s => raizPorId.get(String(s.apolice_id))).filter(Boolean))

  const salvarFiltrosAtuais = () => {
    sessionStorage.setItem('renovacoesFiltros', JSON.stringify({ busca }))
  }
  const maisRecente = (lista) => [...lista].sort((x, y) => new Date(y.created_at) - new Date(x.created_at))[0]

  // "Ver" no menu Ações: leva pra tela cheia de Apólices (mesmo mecanismo abrirApoliceId/voltarPara
  // ja usado por Clientes.jsx <-> Apolices.jsx), com retorno pra Renovações preservando os filtros
  // — mesmo padrao dos botoes de Sinistro/Assistência.
  const irParaApolice = (a) => {
    salvarFiltrosAtuais()
    sessionStorage.setItem('abrirApoliceId', String(a.id))
    sessionStorage.setItem('voltarApoliceLabel', 'Renovações')
    sessionStorage.setItem('voltarPara', '/renovacoes')
    navigate('/apolices')
  }

  const irParaSinistros = (a) => {
    const raizId = raizPorId.get(String(a.id))
    const alvo = maisRecente(sinistros.filter(s => raizPorId.get(String(s.apolice_id)) === raizId))
    salvarFiltrosAtuais()
    if (alvo) sessionStorage.setItem('sinistroAbrirId', String(alvo.id))
    sessionStorage.setItem('sinistroVoltarPara', '/renovacoes')
    navigate('/sinistros')
  }
  const irParaAssistencias = (a) => {
    const raizId = raizPorId.get(String(a.id))
    const alvo = maisRecente(assistencias.filter(s => raizPorId.get(String(s.apolice_id)) === raizId))
    salvarFiltrosAtuais()
    if (alvo) sessionStorage.setItem('assistenciaAbrirId', String(alvo.id))
    sessionStorage.setItem('assistenciaVoltarPara', '/renovacoes')
    navigate('/assistencias')
  }

  // Mesmo padrao de "gerar PDF" ja usado em Indicacoes.jsx: monta uma pagina HTML de impressao numa
  // aba nova e chama window.print() — o usuario salva como PDF pelo dialogo de impressao do
  // navegador, sem precisar de biblioteca de geracao de PDF no backend.
  const gerarPdf = (linhas) => {
    const corPrimaria = config.cor_primaria
    const corClaraHex = corClara(corPrimaria)
    const hoje = new Date().toLocaleDateString('pt-BR')

    const linhasHtml = linhas.map((a, i) => {
      const bd = badgeDias(diasParaVencer(a.data_vencimento))
      const sr = statusRenovacao(a)
      const bg = i % 2 === 0 ? '#fff' : '#FAFAFA'
      const numero = escapeHtml(numeroApolice(a) || '—') + (numeroEndosso(a) ? `<br><span style="font-size:10px;color:#888;">End. ${escapeHtml(numeroEndosso(a))}</span>` : '')
      return `<tr style="background:${bg};">
        <td style="padding:6px 8px;border-bottom:1px solid #eee;text-align:center;">${fmtData(a.data_vencimento)}</td>
        <td style="padding:6px 8px;border-bottom:1px solid #eee;text-align:center;">${sr.key === 'renovado' ? '' : bd.label}</td>
        <td style="padding:6px 8px;border-bottom:1px solid #eee;font-weight:600;">${escapeHtml(a.cliente_nome)}</td>
        <td style="padding:6px 8px;border-bottom:1px solid #eee;text-align:center;white-space:nowrap;">${escapeHtml(a.cliente_cpf_cnpj) || '—'}</td>
        <td style="padding:6px 8px;border-bottom:1px solid #eee;text-align:center;">${escapeHtml(a.ramo) || '—'}</td>
        <td style="padding:6px 8px;border-bottom:1px solid #eee;text-align:center;">${escapeHtml(a.seguradora) || '—'}</td>
        <td style="padding:6px 8px;border-bottom:1px solid #eee;text-align:center;">${numero}</td>
        <td style="padding:6px 8px;border-bottom:1px solid #eee;text-align:center;">${fmtValor(premioApolice(a))}</td>
        <td style="padding:6px 8px;border-bottom:1px solid #eee;text-align:center;">${sr.label}</td>
      </tr>`
    }).join('')

    const html = `<!DOCTYPE html><html><head>
<meta charset="utf-8"><title>Relatório de Renovações</title>
<style>
  body{font-family:Arial,sans-serif;margin:0;padding:0;background:#fff;-webkit-print-color-adjust:exact;print-color-adjust:exact;color:#333;}
  .page{max-width:1100px;margin:0 auto;padding:20px;}
  .hdr{background:${corPrimaria};color:#fff;padding:14px 20px;border-radius:6px;margin-bottom:6px;display:flex;justify-content:space-between;align-items:center;}
  .hdr h1{margin:0;font-size:16px;font-weight:700;}
  .sub{font-size:12px;color:#777;margin-bottom:14px;}
  table{width:100%;border-collapse:collapse;font-size:11px;}
  th{text-align:left;padding:8px;background:${corClaraHex};color:${corPrimaria};font-size:10px;text-transform:uppercase;letter-spacing:.04em;}
  th.c{text-align:center;}
  .footer{text-align:center;font-size:10px;color:#aaa;margin-top:16px;border-top:1px solid #eee;padding-top:10px;}
  @media print{@page{size:A4 landscape;margin:10mm;}.page{padding:0;}}
</style></head><body>
<div class="page">
  <div class="hdr"><h1>RELATÓRIO DE RENOVAÇÕES</h1><div style="font-size:11px;opacity:.85;">${hoje}</div></div>
  <div class="sub">${linhas.length} ${linhas.length === 1 ? 'apólice encontrada' : 'apólices encontradas'}</div>
  <table>
    <thead><tr>
      <th class="c">Final da Vigência</th><th class="c">Dias p/ Vencer</th><th>Segurado</th><th class="c">CPF/CNPJ</th><th class="c">Ramo</th><th class="c">Seguradora</th><th class="c">Apólice/Endosso</th><th class="c">Prêmio Total</th><th class="c">Status</th>
    </tr></thead>
    <tbody>${linhasHtml}</tbody>
  </table>
  <div class="footer">Documento gerado pelo sistema Corretora &mdash; ${hoje}</div>
</div>
<script>setTimeout(()=>window.print(),400)</script>
</body></html>`

    const win = window.open('', '_blank')
    if (win) { win.document.write(html); win.document.close() }
    else avisar('Permita pop-ups para gerar o PDF.')
  }

  const vigentes = apolices.filter(a =>
    a.status === 'emitido' && !!a.data_vencimento && !idsComFilhoEndosso.has(String(a.id))
  )

  const filtroAtivo = temCriterioRenovacoes(filtros)
  const semBusca = !filtroAtivo ? vigentes : vigentes.filter(a => {
    if (filtros.seguradoras.length && !filtros.seguradoras.includes(a.seguradora || '')) return false
    if (filtros.ramos.length && !filtros.ramos.includes(a.ramo || '')) return false
    if (filtros.status.length && !filtros.status.includes(statusRenovacao(a).key)) return false
    const venc = a.data_vencimento ? a.data_vencimento.split('T')[0] : null
    if (filtros.vigDe && (!venc || venc < filtros.vigDe)) return false
    if (filtros.vigAte && (!venc || venc > filtros.vigAte)) return false
    return true
  })

  const q = busca.toLowerCase().trim()
  // Lista abre vazia: so aparece algo depois de buscar ou filtrar.
  const pesquisou = !!q || filtroAtivo
  const filtradas = !pesquisou ? [] : q ? semBusca.filter(a => a.cliente_nome?.toLowerCase().includes(q)) : semBusca

  const sugestoesBusca = (() => {
    if (!q) return []
    const vistos = new Set()
    const sugestoes = []
    for (const a of semBusca) {
      const nome = a.cliente_nome
      if (nome && nome.toLowerCase().includes(q) && !vistos.has(nome)) {
        vistos.add(nome)
        sugestoes.push(nome)
      }
      if (sugestoes.length >= 8) break
    }
    return sugestoes
  })()
  const navBusca = useNavegacaoLista(sugestoesBusca.length, (i) => { setMostrarSugestoesBusca(false); setBusca(sugestoesBusca[i]) })

  const filtradasOrdenadas = sortRows(filtradas, colunasRenovacoes, sortCol, sortDir)
  const paginacao = usePaginacao(filtradasOrdenadas)

  return (
    <>
      <div style={{ padding: '32px 24px 0' }}>
        <h2 style={{ margin: 0, fontSize: 28, fontWeight: 700 }}>Renovações</h2>
        <div style={{ fontSize: 13, color: '#888', marginTop: 4, marginBottom: 20 }}>Apólices e endossos vigentes cujo final da vigência está próximo.</div>
        <div style={{ display: 'flex', gap: 12, marginBottom: 16, alignItems: 'center' }}>
          <div style={{ position: 'relative', maxWidth: 420, width: '100%' }}>
            <input className="form-input" placeholder="Buscar por nome do segurado..." value={busca}
              onChange={e => { setBusca(e.target.value); setMostrarSugestoesBusca(true); navBusca.reset() }}
              onFocus={() => setMostrarSugestoesBusca(true)}
              onBlur={() => setTimeout(() => setMostrarSugestoesBusca(false), 150)}
              onKeyDown={navBusca.onKeyDown}
              style={{ width: '100%', border: '2px solid rgba(var(--primary-rgb),0.15)', borderRadius: 10 }} />
            {mostrarSugestoesBusca && busca.trim() && sugestoesBusca.length > 0 && (
              <div style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, zIndex: 20, background: '#fff', border: '1px solid #D9DDE3', borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,0.12)', maxHeight: 320, overflowY: 'auto' }}>
                {sugestoesBusca.map((nome, i) => (
                  <button key={nome} type="button" onMouseDown={() => { setMostrarSugestoesBusca(false); setBusca(nome) }}
                    onMouseEnter={() => navBusca.setIndice(i)}
                    style={{ width: '100%', textAlign: 'left', border: 'none', background: i === navBusca.indice ? 'rgba(var(--primary-rgb),0.08)' : '#fff', padding: '10px 12px', cursor: 'pointer', borderBottom: '1px solid #F1F3F5', fontSize: 13, color: '#1A1A2E' }}>
                    {nome}
                  </button>
                ))}
              </div>
            )}
          </div>
          <button type="button" className="btn btn-secondary" style={{ flexShrink: 0, whiteSpace: 'nowrap' }} onClick={() => setUsarFiltro(v => !v)}>
            {usarFiltro ? 'Fechar filtros' : 'Abrir filtros'}{temCriterioRenovacoes(filtros) && <span style={{ color: 'var(--primary)', marginLeft: 6 }}>●</span>}
          </button>
          <div style={{ flex: 1 }} />
          <button type="button" className="btn btn-primary" style={{ flexShrink: 0 }} onClick={() => gerarPdf(filtradasOrdenadas)} disabled={filtradasOrdenadas.length === 0}>
            Gerar PDF
          </button>
        </div>
        {usarFiltro && (
          <div className="card" style={{ marginBottom: 16, padding: 16 }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 12 }}>
              <SelecaoMultipla label="Seguradora" opcoes={seguradoras.map(v => ({ valor: v, label: v }))} valores={filtros.seguradoras} onChange={v => setFiltro('seguradoras', v)} />
              <SelecaoMultipla label="Ramo" opcoes={ramos.map(v => ({ valor: v, label: v }))} valores={filtros.ramos} onChange={v => setFiltro('ramos', v)} />
              <SelecaoMultipla label="Status" opcoes={Object.entries(STATUS_RENOVACAO).map(([valor, st]) => ({ valor, label: st.label }))} valores={filtros.status} onChange={v => setFiltro('status', v)} />
              <div />
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">Final da vigência de</label>
                <input className="form-input" type="date" value={filtros.vigDe} onChange={e => setFiltro('vigDe', e.target.value)} />
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">até</label>
                <input className="form-input" type="date" value={filtros.vigAte} onChange={e => setFiltro('vigAte', e.target.value)} />
              </div>
              <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                <button type="button" className="btn btn-secondary" disabled={!temCriterioRenovacoes(filtros)} onClick={() => setFiltros(FILTROS_RENOVACOES_VAZIOS)}>Limpar filtros</button>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="page-content" style={{ paddingTop: 0 }}>
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 12, marginBottom: 16 }}>
            <span style={{ fontSize: 12, color: '#888' }}>
              {filtradas.length} {filtradas.length === 1 ? 'apólice' : 'apólices'}
            </span>
          </div>

          <div className="table-wrap" ref={tableWrapRef}>
            <table className="table-borda-destaque table-compacta">
              <thead>
                <tr>
                  {colunasRenovacoes.map(c => (
                    <SortableTh key={c.key} colKey={c.key} sortCol={sortCol} sortDir={sortDir} onSort={toggleSort}>{c.label}</SortableTh>
                  ))}
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {paginacao.itensPagina.map(a => {
                  const dias = diasParaVencer(a.data_vencimento)
                  const bd = badgeDias(dias)
                  const raizId = raizPorId.get(String(a.id))
                  const temSinistro = raizesComSinistro.has(raizId)
                  const temAssistencia = raizesComAssistencia.has(raizId)
                  const sr = statusRenovacao(a)
                  const cardCrm = cardCrmPorApoliceId.get(String(a.id))
                  const cadeia = cadeiaDaLinha(a)
                  return (
                    <tr key={a.id}>
                      <td style={{ textAlign: 'center' }}>{fmtData(a.data_vencimento)}</td>
                      <td style={{ textAlign: 'center' }}>{sr.key === 'renovado' ? '' : <span className={`badge ${bd.cls}`}>{bd.label}</span>}</td>
                      <td style={{ fontWeight: 600 }}>{a.cliente_nome}</td>
                      <td style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>{a.cliente_cpf_cnpj || '—'}</td>
                      <td style={{ textAlign: 'center' }}>{a.ramo || '—'}</td>
                      <td style={{ textAlign: 'center' }}>{a.seguradora || '—'}</td>
                      <td style={{ textAlign: 'center' }}>
                        {numeroApolice(a) || '—'}
                        {numeroEndosso(a) && <div style={{ fontSize: 11, color: '#888' }}>End. {numeroEndosso(a)}</div>}
                      </td>
                      <td style={{ textAlign: 'center' }}>{fmtValor(premioApolice(a))}</td>
                      <td style={{ textAlign: 'center' }}>
                        <BadgeStatus st={sr} />
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: 6 }}>
                          <div ref={pdfMenu?.id === a.id ? pdfMenuRef : null}>
                            <button type="button" className="btn btn-secondary" title={cadeia.length > 1 ? 'Ver PDF da apólice / endossos' : 'Ver PDF da apólice'}
                              onClick={(e) => {
                                if (cadeia.length === 1) { verPdfDocumento(cadeia[0]); return }
                                if (pdfMenu?.id === a.id) { setPdfMenu(null); return }
                                const r = e.currentTarget.getBoundingClientRect()
                                const abrirCima = window.innerHeight - r.bottom < 40 + cadeia.length * 34 && r.top > window.innerHeight - r.bottom
                                setPdfMenu({ id: a.id, pos: { left: r.left, ...(abrirCima ? { bottom: window.innerHeight - r.top + 4 } : { top: r.bottom + 4 }) } })
                              }}
                              style={{ width: 28, height: 28, padding: 0, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--primary)' }}>
                              <IconVisualizar size={16} />
                            </button>
                            {pdfMenu?.id === a.id && (
                              <div style={{
                                position: 'fixed', zIndex: 9999, ...pdfMenu.pos,
                                background: '#fff', border: '1px solid #e0e0e0', borderRadius: 6,
                                boxShadow: '0 2px 8px rgba(0,0,0,0.12)', minWidth: 160, overflow: 'hidden'
                              }}>
                                {cadeia.map((d, i) => (
                                  <button key={d.id} type="button" onClick={() => { setPdfMenu(null); verPdfDocumento(d) }} style={{
                                    display: 'block', width: '100%', textAlign: 'left', padding: '8px 12px',
                                    background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, whiteSpace: 'nowrap'
                                  }} onMouseEnter={e => e.currentTarget.style.background = '#f5f5f5'} onMouseLeave={e => e.currentTarget.style.background = 'none'}>
                                    {i === 0 ? 'PDF da Apólice' : `PDF do Endosso ${d.emissao_numero_endosso || i}`}
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                          <div ref={acoesAbertoId === a.id ? acoesMenuRef : null} style={{ position: 'relative' }}>
                            <button type="button" className="btn btn-secondary" style={{ width: 28, height: 28, padding: 0, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--primary)' }} onClick={(e) => {
                              if (acoesAbertoId === a.id) { setAcoesAbertoId(null); return }
                              // Abre pra cima so quando ha mais espaco na janela ali do que embaixo —
                              // usa a janela inteira (nao o table-wrap) porque o menu e' position:fixed.
                              const rectBotao = e.currentTarget.getBoundingClientRect()
                              const espacoAbaixo = window.innerHeight - rectBotao.bottom
                              const espacoAcima = rectBotao.top
                              const abrirCima = espacoAbaixo < 90 && espacoAcima > espacoAbaixo
                              setAcoesMenuPos({
                                right: window.innerWidth - rectBotao.right,
                                ...(abrirCima ? { bottom: window.innerHeight - rectBotao.top + 4 } : { top: rectBotao.bottom + 4 }),
                              })
                              setAcoesAbertoId(a.id)
                            }} title="Ações"><IconAcoes size={16} /></button>
                            {acoesAbertoId === a.id && acoesMenuPos && (
                              <div style={{
                                position: 'fixed', zIndex: 9999, ...acoesMenuPos,
                                background: '#fff', border: '1px solid #e0e0e0', borderRadius: 6,
                                boxShadow: '0 2px 8px rgba(0,0,0,0.12)', minWidth: 160, overflow: 'hidden'
                              }}>
                                <button type="button" onClick={() => { setAcoesAbertoId(null); irParaApolice(a) }} style={{
                                  display: 'block', width: '100%', textAlign: 'left', padding: '8px 12px',
                                  background: 'none', border: 'none', cursor: 'pointer', fontSize: 13
                                }} onMouseEnter={e => e.currentTarget.style.background = '#f5f5f5'} onMouseLeave={e => e.currentTarget.style.background = 'none'}>Ver</button>
                                <button type="button" onClick={() => { setAcoesAbertoId(null); cardCrm ? abrirCardCrm(cardCrm) : gerarCardRenovacao(a) }} style={{
                                  display: 'block', width: '100%', textAlign: 'left', padding: '8px 12px',
                                  background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, whiteSpace: 'nowrap'
                                }} onMouseEnter={e => e.currentTarget.style.background = '#f5f5f5'} onMouseLeave={e => e.currentTarget.style.background = 'none'}>{cardCrm ? 'Abrir card no CRM' : 'Gerar Card Renov.'}</button>
                              </div>
                            )}
                          </div>
                          {temSinistro && (
                            <button type="button" title="Teve sinistro durante a vigência" onClick={() => irParaSinistros(a)}
                              style={{ width: 28, height: 28, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', border: 'none', borderRadius: 6, background: 'var(--danger)', color: '#fff', cursor: 'pointer' }}>
                              <IconSinistro size={16} />
                            </button>
                          )}
                          {temAssistencia && (
                            <button type="button" title="Teve assistência durante a vigência" onClick={() => irParaAssistencias(a)}
                              style={{ width: 28, height: 28, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', border: 'none', borderRadius: 6, background: '#EAB308', color: '#fff', cursor: 'pointer' }}>
                              <IconAssistencia size={16} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  )
                })}
                {!carregando && filtradasOrdenadas.length === 0 && (
                  <tr><td colSpan={colunasRenovacoes.length + 1} style={{ textAlign: 'center', color: '#aaa', padding: 32 }}>{pesquisou ? 'Nenhuma apólice vencendo encontrada' : 'Use a busca ou o Filtro para ver as apólices.'}</td></tr>
                )}
              </tbody>
            </table>
          </div>
          <Paginacao {...paginacao} rotulo="apólice" />
        </div>
      </div>
    </>
  )
}
